// Advanced Responsive Design Utilities for 2025
import { css } from 'styled-components';
export const ResponsiveBreakpoints = {
  // Mobile-first breakpoints
  mobile: 320,
  mobileLarge: 414,
  tablet: 768,
  tabletLarge: 1024,
  desktop: 1200,
  desktopLarge: 1440,
  wide: 1920,
  ultraWide: 2560,
} as const;

export const mediaQueries = {
  mobile: `@media (max-width: ${ResponsiveBreakpoints.mobileLarge}px)`,
  mobileUp: `@media (min-width: ${ResponsiveBreakpoints.mobile}px)`,
  tablet: `@media (max-width: ${ResponsiveBreakpoints.tablet}px)`,
  tabletUp: `@media (min-width: ${ResponsiveBreakpoints.tablet}px)`,
  tabletOnly: `@media (min-width: ${ResponsiveBreakpoints.tablet}px) and (max-width: ${ResponsiveBreakpoints.tabletLarge - 1}px)`,
  desktop: `@media (min-width: ${ResponsiveBreakpoints.desktop}px)`,
  desktopUp: `@media (min-width: ${ResponsiveBreakpoints.desktop}px)`,
  wide: `@media (min-width: ${ResponsiveBreakpoints.wide}px)`,
  ultraWide: `@media (min-width: ${ResponsiveBreakpoints.ultraWide}px)`,
};

// Legacy Responsive Typography Scale (strings only)
export const responsiveTypographyStrings = {
  // Fluid typography using clamp()
  displayLarge: 'clamp(2.5rem, 4vw, 4rem)',
  displayMedium: 'clamp(2rem, 3vw, 3rem)',
  displaySmall: 'clamp(1.75rem, 2.5vw, 2.25rem)',
  headlineLarge: 'clamp(1.5rem, 2vw, 2rem)',
  headlineMedium: 'clamp(1.25rem, 1.5vw, 1.75rem)',
  headlineSmall: 'clamp(1.125rem, 1.25vw, 1.5rem)',
  titleLarge: 'clamp(1rem, 1.125vw, 1.375rem)',
  titleMedium: 'clamp(0.875rem, 1vw, 1.125rem)',
  titleSmall: 'clamp(0.875rem, 0.875vw, 1rem)',
  bodyLarge: 'clamp(0.875rem, 0.875vw, 1rem)',
  bodyMedium: 'clamp(0.75rem, 0.75vw, 0.875rem)',
  bodySmall: 'clamp(0.6875rem, 0.6875vw, 0.75rem)',
  labelLarge: 'clamp(0.75rem, 0.75vw, 0.875rem)',
  labelMedium: 'clamp(0.6875rem, 0.6875vw, 0.75rem)',
  labelSmall: 'clamp(0.625rem, 0.625vw, 0.6875rem)',
};

// Responsive Spacing Scale
export const responsiveSpacing = {
  xs: 'clamp(0.25rem, 0.5vw, 0.5rem)',
  sm: 'clamp(0.5rem, 1vw, 1rem)',
  md: 'clamp(0.75rem, 1.5vw, 1.5rem)',
  lg: 'clamp(1rem, 2vw, 2rem)',
  xl: 'clamp(1.25rem, 2.5vw, 2.5rem)',
  xxl: 'clamp(1.5rem, 3vw, 3rem)',
  xxxl: 'clamp(2rem, 4vw, 4rem)',
};

// Grid System Configuration
export const gridSystem = {
  container: {
    maxWidth: '1440px',
    padding: {
      mobile: '1rem',
      tablet: '2rem',
      desktop: '3rem',
    },
  },
  columns: 12,
  gutter: {
    mobile: '1rem',
    tablet: '1.5rem',
    desktop: '2rem',
  },
};

// Touch-friendly sizing for mobile
export const touchTargets = {
  minimum: '44px', // iOS/Android minimum
  comfortable: '48px', // Recommended
  large: '56px', // Large touch targets
};

// Responsive utilities
export const ResponsiveUtils = {
  // Check if device is mobile based on screen width
  isMobile: (): boolean => {
    return typeof window !== 'undefined' && window.innerWidth <= ResponsiveBreakpoints.tablet;
  },

  // Check if device is tablet
  isTablet: (): boolean => {
    return typeof window !== 'undefined' && 
           window.innerWidth > ResponsiveBreakpoints.tablet && 
           window.innerWidth <= ResponsiveBreakpoints.desktop;
  },

  // Check if touch device
  isTouchDevice: (): boolean => {
    return typeof window !== 'undefined' && (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-ignore
      navigator.msMaxTouchPoints > 0
    );
  },

  // Get optimal grid columns for screen size
  getGridColumns: (screenWidth: number): number => {
    if (screenWidth < ResponsiveBreakpoints.tablet) return 1;
    if (screenWidth < ResponsiveBreakpoints.desktop) return 2;
    if (screenWidth < ResponsiveBreakpoints.wide) return 3;
    return 4;
  },

  // Get optimal card size for screen
  getCardSize: (screenWidth: number): { minWidth: string; maxWidth: string } => {
    if (screenWidth < ResponsiveBreakpoints.tablet) {
      return { minWidth: '100%', maxWidth: '100%' };
    }
    if (screenWidth < ResponsiveBreakpoints.desktop) {
      return { minWidth: '280px', maxWidth: '1fr' };
    }
    return { minWidth: '320px', maxWidth: '1fr' };
  },

  // Progressive image loading based on screen size
  getOptimalImageSize: (screenWidth: number): 'small' | 'medium' | 'large' => {
    if (screenWidth < ResponsiveBreakpoints.tablet) return 'small';
    if (screenWidth < ResponsiveBreakpoints.desktop) return 'medium';
    return 'large';
  },
};

// Container query support (future-proof)
export const containerQueries = {
  small: '@container (max-width: 400px)',
  medium: '@container (min-width: 401px) and (max-width: 800px)',
  large: '@container (min-width: 801px)',
};

// Dark mode and theme utilities
export const themeAdaptive = {
  // System preference detection
  prefersLight: '@media (prefers-color-scheme: light)',
  prefersDark: '@media (prefers-color-scheme: dark)',
  
  // Accessibility preferences
  prefersReducedMotion: '@media (prefers-reduced-motion: reduce)',
  prefersHighContrast: '@media (prefers-contrast: high)',
  prefersReducedData: '@media (prefers-reduced-data: reduce)',
};

// Styled-components compatible media queries

export const media = {
  mobile: (styles: any) => css`
    @media (max-width: ${ResponsiveBreakpoints.mobileLarge}px) {
      ${styles}
    }
  `,
  sm: (styles: any) => css`
    @media (min-width: ${ResponsiveBreakpoints.tablet}px) {
      ${styles}
    }
  `,
  tablet: (styles: any) => css`
    @media (min-width: ${ResponsiveBreakpoints.tablet}px) {
      ${styles}
    }
  `,
  lg: (styles: any) => css`
    @media (min-width: ${ResponsiveBreakpoints.desktop}px) {
      ${styles}
    }
  `,
  desktop: (styles: any) => css`
    @media (min-width: ${ResponsiveBreakpoints.desktop}px) {
      ${styles}
    }
  `,
  wide: (styles: any) => css`
    @media (min-width: ${ResponsiveBreakpoints.wide}px) {
      ${styles}
    }
  `,
};

// Enhanced responsive typography with CSS-in-JS compatibility
export const responsiveTypography = {
  // Display styles
  hero: css`
    font-size: clamp(2.5rem, 4vw, 4rem);
    line-height: 1.1;
    font-weight: 700;
  `,
  display: css`
    font-size: clamp(2rem, 3vw, 3rem);
    line-height: 1.2;
    font-weight: 600;
  `,
  
  // Heading styles
  h1: css`
    font-size: clamp(1.75rem, 2.5vw, 2.25rem);
    line-height: 1.2;
    font-weight: 600;
  `,
  h2: css`
    font-size: clamp(1.5rem, 2vw, 2rem);
    line-height: 1.3;
    font-weight: 600;
  `,
  h3: css`
    font-size: clamp(1.25rem, 1.5vw, 1.75rem);
    line-height: 1.3;
    font-weight: 600;
  `,
  h4: css`
    font-size: clamp(1.125rem, 1.25vw, 1.5rem);
    line-height: 1.4;
    font-weight: 500;
  `,
  h5: css`
    font-size: clamp(1rem, 1.125vw, 1.375rem);
    line-height: 1.4;
    font-weight: 500;
  `,
  h6: css`
    font-size: clamp(0.875rem, 1vw, 1.125rem);
    line-height: 1.4;
    font-weight: 500;
  `,
  
  // Body text styles
  body: css`
    font-size: clamp(0.875rem, 0.875vw, 1rem);
    line-height: 1.6;
    font-weight: 400;
  `,
  bodyLarge: css`
    font-size: clamp(1rem, 1vw, 1.125rem);
    line-height: 1.6;
    font-weight: 400;
  `,
  bodySmall: css`
    font-size: clamp(0.75rem, 0.75vw, 0.875rem);
    line-height: 1.5;
    font-weight: 400;
  `,
  
  // Label and caption styles
  label: css`
    font-size: clamp(0.75rem, 0.75vw, 0.875rem);
    line-height: 1.4;
    font-weight: 500;
  `,
  caption: css`
    font-size: clamp(0.6875rem, 0.6875vw, 0.75rem);
    line-height: 1.3;
    font-weight: 400;
  `,
};

// Responsive grid utilities
export const responsiveGrid = {
  cols1: css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
  `,
  cols2: css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    
    @media (min-width: ${ResponsiveBreakpoints.tablet}px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;
    }
  `,
  cols3: css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    
    @media (min-width: ${ResponsiveBreakpoints.tablet}px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;
    }
    
    @media (min-width: ${ResponsiveBreakpoints.desktop}px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 2rem;
    }
  `,
  cols4: css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    
    @media (min-width: ${ResponsiveBreakpoints.tablet}px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem;
    }
    
    @media (min-width: ${ResponsiveBreakpoints.desktop}px) {
      grid-template-columns: repeat(4, 1fr);
      gap: 2rem;
    }
  `,
  autoFit: (minWidth = '280px') => css`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(${minWidth}, 1fr));
    gap: 1rem;
    
    @media (min-width: ${ResponsiveBreakpoints.tablet}px) {
      gap: 1.5rem;
    }
    
    @media (min-width: ${ResponsiveBreakpoints.desktop}px) {
      gap: 2rem;
    }
  `,
};